<template>
  <admin-input-list-wrapper :is-fetched="isFetched">
    <template #search>
      <data-input
        :data-type="{ name: 'text' }"
        prepend-inner-icon="mdi-magnify"
        :placeholder="$i18n.t('default.find')"
        v-model="search"
        clearable
        height="32"
        hide-details
        :disabled="disabled"
        class="searchInput"
      >
      </data-input>
    </template>
    <template #button>
      <v-row no-gutters justify="start" v-if="hasAddButton" class="flex-nowrap mb-2" style="width: 100%">
        <v-col cols="auto">
          <base-button translation-path="button.addSource" @click="showAddNewDatasource()"></base-button>
        </v-col>
        <v-col cols="auto" class="ml-auto">
          <base-button translation-path="button.importFileWithData" cancel @click="showAddDatasource()"></base-button>
        </v-col>
      </v-row>
    </template>
    <template #list>
      <v-row no-gutters>
        <v-col>
          <v-list dense :disabled="disabled">
            <v-list-item
              v-for="datasource in searchedItems"
              :key="datasource.name"
              class="layer-element px-2 hover-list-element"
              style="height: 45px"
              :disabled="disabled"
            >
              <v-list-item-icon>
                <v-icon style="font-size: 21px" class="mr-2" color="rgba(0,0,0,0.7)">
                  {{ getDataSourceIcon(datasource.geometry_type) }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title
                  :title="datasource.verbose_name"
                  v-text="datasource.verbose_name"
                  style="width: 0"
                  class="text-body-2 font-weight-regular text-truncate"
                />
              </v-list-item-content>
              <v-list-item-action>
                <dots-menu
                  font-size="19px"
                  :items="datasource.dotsItems"
                  @delete="showDeleteDatasource(datasource.name)"
                  @editAttributes="showEditAttributes(datasource)"
                  @editName="showEditName(datasource)"
                  :disabled="disabled"
                ></dots-menu>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </template>
    <template #dialogs>
      <main-input-dialog
        v-if="isAddingDatasourceLoaded"
        :is-visible.sync="isAddingDatasourceVisible"
        :title-translation="$i18n.t('dialog.addDataSource')"
        agree-text="dialog.agreeAdd"
        @agree="handleAddDatasource"
        @modalClosed="handleCloseModal"
        :inputs="addDataSourceInputs"
        dialog-width="45%"
        ref="adding"
      >
        <template #[`srid.input`]="data">
          <v-col cols="6">
            <data-input v-model="addDataSourceInputs.find(iDSI => iDSI.name === 'srid').value" v-bind="data" />
          </v-col>
        </template>
        <template #[`file.input`]>
          <v-col cols="6">
            <file-input
              ref="file"
              :options="dropzoneOptions"
              :show-added-files="true"
              :expanded-files-extensions="filesExtensions"
              :button-subheaders="fileInputSubheadersTranslations"
              :events="{
                'vdropzone-sending-multiple': sendingEvent,
                'vdropzone-success-multiple': onUploadSuccess,
              }"
            ></file-input>
          </v-col>
        </template>
      </main-input-dialog>
      <main-dialog
        v-if="isDeleteDatasourceLoaded"
        :is-visible.sync="isDeleteDatasourceVisible"
        :title="$i18n.t('dialog.deleteDatasource')"
        :body="$i18n.t('dialog.confirmDatasourceDelete')"
        agree-text="dialog.agreeDelete"
        @agree="handleDeleteDatasource()"
      ></main-dialog>
      <main-input-dialog
        v-if="isEditNameLoaded"
        :is-visible.sync="isEditNameVisible"
        :title-translation="$i18n.t('dialog.title.changeDataSourceName')"
        agree-text="dialog.agreeSave"
        :is-input-dialog="true"
        @agree="handleEditName"
        :inputs="changeDataSourceNameInput"
      >
        <template #[`name.input`]="slot">
          <v-col>
            <v-row no-gutters>
              <v-col cols="12">
                <data-input
                  v-model="editingDataSource.verbose_name"
                  clearable
                  :data-type="slot.dataType"
                  :rules="slot.rules"
                />
              </v-col>
            </v-row>
          </v-col>
        </template>
      </main-input-dialog>
      <new-data-source-dialog v-if="isAddingNewDatasourceLoaded" :is-visible.sync="isAddingNewDatasourceVisible" />
      <data-source-attributes-editing
        v-if="isEditAttributesLoaded"
        :is-visible.sync="isEditAttributesVisible"
        :editing-data-source.sync="editingDataSource"
      />
    </template>
  </admin-input-list-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import stylePreviewUtilities from '@/mixins/stylePreviewUtilities';

import AdminInputListWrapper from '@/components/AdminInputListWrapper';
import DotsMenu from '@/components/DotsMenu';
import FileInput from '@/components/FileInput';
import DataInput from '@/components/DataInput';
import BaseButton from '@/components/BaseButton';

export default {
  name: 'DataSourcesSettingsList',
  mixins: [stylePreviewUtilities],
  props: {
    dataSources: {
      type: Array,
      required: true,
    },
    isFetched: {
      type: Boolean,
      default: false,
    },
    hasAddButton: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    DataInput,
    BaseButton,
    DotsMenu,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    FileInput,
    NewDataSourceDialog: () => import('@/components/NewDataSourceDialog'),
    DataSourceAttributesEditing: () => import('@/components/DataSourceAttributesEditing'),
    AdminInputListWrapper,
  },
  data: () => ({
    search: '',
    editingDataSource: {},
    isAddingDatasourceVisible: false,
    isAddingDatasourceLoaded: false,
    isAddingNewDatasourceVisible: false,
    isAddingNewDatasourceLoaded: false,
    isEditNameVisible: false,
    isEditNameLoaded: false,
    isEditAttributesVisible: false,
    isEditAttributesLoaded: false,
    isDeleteDatasourceVisible: false,
    isDeleteDatasourceLoaded: false,
    nameToDelete: undefined,
    filesType: '',
  }),
  computed: {
    datasourceNameMaxLength: get('admin/settingsValues@datasource_name_max_length'),
    coordinatesEpsgs: get('map/coordinatesEpsgs'),
    selectedDataSources: get('datasourceSettings/selectedDataSources'),
    isDxfUploadEnabled: get('admin/additionalModules@DXF_UPLOADS.enabled'),
    isGmlUploadEnabled: get('admin/additionalModules@GML_UPLOADS.enabled'),
    computedDataSources() {
      return this.dataSources.map(dataSource => {
        const isSelected = this.selectedDataSources.findIndex(dS => dS.datasource === dataSource.name) > -1;
        return {
          ...dataSource,
          dotsItems: [
            { name: 'editAttributes' },
            { name: 'editName' },
            ...(dataSource.predefined || isSelected ? [] : [{ name: 'delete' }]),
          ],
        };
      });
    },
    computedEpsgItems() {
      return this.$_objectToArray(this.coordinatesEpsgs);
    },
    addDataSourceInputs() {
      return [
        {
          name: 'dataSourceName',
          value: '',
          translationPath: 'dialog.dataSourceName',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: this.datasourceNameMaxLength,
          },
        },
        {
          name: 'srid',
          value: undefined,
          translationPath: 'dialog.body.coordinateSystem',
          rules: [v => (this.computedEpsgItems.find(item => item.srid == v) ? true : false || 'rules.required')],
          dataType: {
            name: 'autocomplete',
          },
          fixedHeight: false,
          items: this.computedEpsgItems,
          itemValue: 'srid',
        },
        {
          name: 'file',
          translationPath: 'dialog.file',
          align: 'start',
          isCustom: true,
        },
      ];
    },
    changeDataSourceNameInput() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.body.name',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: this.datasourceNameMaxLength,
          },
        },
      ];
    },
    dropzoneOptions() {
      const self = this;
      return {
        url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/shp`,
        method: 'post',
        autoProcessQueue: false,
        acceptedFiles: self.getAcceptedFiles(),
        uploadMultiple: true,
        maxFiles: 3,
        parallelUploads: 3,
        paramName(i) {
          const files = self.$refs.file.$refs.dropzone.getAcceptedFiles();
          const file = files[i];
          return file.name.split('.').pop();
        },
      };
    },
    filesExtensions() {
      return [
        {
          name: 'shapefile',
          url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/shp`,
          exts: ['shp', 'shx', 'dbf'],
          canExtDuplicate: false,
          minFiles: 3,
          maxFiles: 3,
        },
        {
          name: 'geojson',
          url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/geojson`,
          exts: ['geojson'],
          canExtDuplicate: false,
          minFiles: 1,
          maxFiles: 1,
        },
        ...(this.isDxfUploadEnabled
          ? [
              {
                name: 'dxf',
                url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/dxf`,
                exts: ['dxf'],
                canExtDuplicate: false,
                minFiles: 1,
                maxFiles: 1,
              },
            ]
          : []),
        ...(this.isGmlUploadEnabled
          ? [
              {
                name: 'gml',
                url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/gml`,
                exts: ['gml'],
                canExtDuplicate: false,
                minFiles: 1,
                maxFiles: 1,
              },
            ]
          : []),
      ];
    },
    fileInputSubheadersTranslations() {
      let exts = 'Shapefile(dbf, shp, shx)/GeoJSON';
      if (this.isDxfUploadEnabled) {
        exts += '/DXF';
      }
      if (this.isGmlUploadEnabled) {
        exts += '/GML';
      }
      return [
        { type: 'straight', value: this.$i18n.t('dialog.dataSourceRequiredFiles', { value: exts }) },
        { type: 'path', value: 'dialog.dataSourceCoding' },
      ];
    },
    searchedItems() {
      if (!this.search) {
        return this.sortItems();
      }
      return this.sortItems().filter(a => a.verbose_name.toLowerCase().includes(this.search.toLowerCase()));
    },
  },
  methods: {
    deleteDataSource: call('layers/deleteDataSource'),
    editDataSource: call('layers/editDataSourceMetadata'),
    getDataSources: call('layers/getLayersMetadata'),
    getDatabaseSrid: call('map/getDatabaseSrid'),
    showAddDatasource() {
      this.isAddingDatasourceLoaded = true;
      this.isAddingDatasourceVisible = true;
    },
    showAddNewDatasource() {
      this.isAddingNewDatasourceVisible = true;
      this.isAddingNewDatasourceLoaded = true;
    },
    showEditAttributes(datasource) {
      this.editingDataSource = JSON.parse(JSON.stringify(datasource));
      this.isEditAttributesLoaded = true;
      this.isEditAttributesVisible = true;
    },
    showEditName(datasource) {
      this.editingDataSource = { ...datasource };
      this.isEditNameLoaded = true;
      this.isEditNameVisible = true;
    },
    showDeleteDatasource(name) {
      this.nameToDelete = name;
      this.isDeleteDatasourceLoaded = true;
      this.isDeleteDatasourceVisible = true;
    },
    handleAddDatasource() {
      if (this.$refs.file.$refs.dropzone.getAcceptedFiles().length < 1) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.filesRequired'),
        });
      } else if (!this.addDataSourceInputs.find(aDSI => aDSI.name === 'dataSourceName').value) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.nameRequired'),
        });
      } else if (!this.addDataSourceInputs.find(aDSI => aDSI.name === 'srid').value) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.sridRequired'),
        });
      } else {
        this.$refs.file.$refs.dropzone.processQueue();
      }
    },
    handleCloseModal() {
      this.$refs.file.$refs.dropzone.removeAllFiles();
    },
    handleDeleteDatasource() {
      this.deleteDataSource(this.nameToDelete);
      this.isDeleteDatasourceVisible = false;
    },
    handleEditName() {
      const payload = {
        name: this.editingDataSource.name,
        body: {
          verbose_name: this.editingDataSource.verbose_name,
        },
      };
      this.editDataSource(payload);
      this.isEditNameVisible = false;
    },
    sendingEvent(files, xhr, formData) {
      formData.append('datasource_name', this.addDataSourceInputs.find(aDSI => aDSI.name === 'dataSourceName').value);
      formData.append('srid', this.addDataSourceInputs.find(aDSI => aDSI.name === 'srid').value);
    },
    onUploadSuccess() {
      this.$refs.adding.$refs.form.reset();
      this.isAddingDatasourceVisible = false;
      this.$refs.file.$refs.dropzone.removeAllFiles();
      this.getDataSources({ with_attributes_schema: true });
    },
    sortItems() {
      return this.computedDataSources?.sort((a, b) => a.verbose_name.localeCompare(b.verbose_name));
    },
    getAcceptedFiles() {
      const files = ['.shp', '.dbf', '.shx', '.geojson'];
      if (this.isDxfUploadEnabled) files.push('.dxf');
      if (this.isGmlUploadEnabled) files.push('.gml');
      return files.join(',');
    },
  },
  mounted() {
    this.getDatabaseSrid();
  },
};
</script>

<style lang="scss" scoped>
.layer-element {
  @include border();
  text-align: left;
}
::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
